// Shared by the browser (lib/db.ts) and the Worker (lib/d1-core.ts). Pure functions, no imports.
//
// WHY: every collection has ONE change counter (col_versions). `chats`, `posts`, `friendRequests`, `gifts` ... change
// whenever ANYONE writes to them, so every signed-in person's listener re-ran its whole query each time somebody else
// liked a post or sent a message. A "scoped" counter narrows that: a query such as
//     friendRequests  where to == <me>
// only has to re-run when a friendRequests doc whose `to` is <me> is written (before OR after the write).
// The same key is also the "doorbell" channel of the real-time hub (see custom-worker.ts / lib/db.ts).

/** Fields we keep a per-value counter for. Only fields the app really filters on with ==, array-contains. */
export const SCOPE_FIELDS = [
  'authorId', 'uid', 'userId', 'from', 'to', 'fromId', 'toId', 'toUid', 'subscriberId', 'creatorId',
  'members', 'memberIds', 'groupId', 'calleeId', 'answererId',
] as const

const SCOPE_SET = new Set<string>(SCOPE_FIELDS)
const MAX_VALUE_LEN = 120 // longer values are never scoped (query side falls back to the plain collection key)
// Guild member lists are capped at 100 + 50 per bonus purchase (lib/premium-plans.ts), calls at 100, so 500 is never reached.
const MAX_ARRAY_KEYS = 500

const scalar = (v: any): string | null => {
  if (typeof v === 'string') return v.length > 0 && v.length <= MAX_VALUE_LEN ? v : null
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return null
}

export const scopeKey = (parent: string, field: string, value: string) => `${parent}#${field}=${value}`

/** All scoped counter keys a document (in ONE state: before or after a write) belongs to. */
export function scopeKeysForDoc(parent: string, data: Record<string, any> | null | undefined): string[] {
  if (!data) return []
  const out: string[] = []
  for (const f of SCOPE_FIELDS) {
    const v = data[f]
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      let n = 0
      for (const x of v) {
        const s = scalar(x)
        if (s === null) continue
        out.push(scopeKey(parent, f, s))
        if (++n >= MAX_ARRAY_KEYS) break
      }
    } else {
      const s = scalar(v)
      if (s !== null) out.push(scopeKey(parent, f, s))
    }
  }
  return out
}

type Spec = { parent: string; filters?: { f: string; op: string; v: any }[] }

/**
 * The counter key a QUERY should watch. The first `==` / `array-contains` filter on a scope field with a short
 * scalar value wins; anything else watches the whole collection (always correct, just broader).
 */
export function queryKey(spec: Spec): string {
  for (const f of spec.filters || []) {
    if (!f || !SCOPE_SET.has(f.f)) continue
    if (f.op === '==') {
      const s = scalar(f.v)
      if (s !== null) return scopeKey(spec.parent, f.f, s)
    } else if (f.op === 'array-contains') {
      const s = scalar(f.v)
      if (s !== null) return scopeKey(spec.parent, f.f, s)
    }
  }
  return spec.parent
}

/**
 * Should this listener get a push "doorbell"? Only NARROW channels do: a scoped key, or a sub-collection
 * (chats/{id}/messages, posts/{id}/comments, notifications/{uid}/items ...). A top-level, unscoped collection such as
 * `posts` changes on every like of every post for everybody - pushing that would ring every phone constantly, so those
 * keep their normal polling.
 */
export function isNarrowKey(key: string): boolean {
  if (key.includes('#')) return true
  return key.split('/').length >= 3
}

/** Doc listeners: narrow too, except presence heartbeats (every online user writes them all the time). */
export const isNarrowDocPath = (path: string) => !/^presence(\/|$)/.test(path)

/** 32-bit FNV-1a: a channel name is sent to the hub as this number (small, fits in the socket attachment). */
export function hashKey(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}
